import { Modal, ModalContent } from '@heroui/react';
import {
  ArrowRight, Check, Copy, LoaderCircle, X,
} from 'lucide-react';
import {
  FC, ReactNode, useEffect, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { create } from 'zustand';

import { auth } from '@/config/firebase';
import { useGetOrganizationsByIds } from '@/queries/useGetOrganizationsByIds';
import { useOrganizationState, useUserState } from '@/state';

import aiPhoto from '../../assets/images/ads/ad-audit-ai.webp';
import positionsPhoto from '../../assets/images/ads/ad-audit-positions.webp';
import { AUDIT_AD_HIDDEN_UNTIL_KEY } from './AuditAdBar.constants';
import { AdCardRoot, AdRoot } from './AuditAdBar.styled';

/*
 * Autoreklama audytu hotelspot (audit.hotelspot.pl): widoczność hotelu w AI i pozycje w Google.
 * Wygląd jak HotelspotAdBar z hotelspot-audit (te same klasy hsad-*, kolory, gwiazdka, zdjęcie), w pionie.
 *
 * - AuditAdModalHost (w Sidebarze, zawsze zamontowany): interaktywny popup. „Odbierz kod +1000 tokenów” woła
 *   v3-auditAccount panelGift (wspólny backend, giftCards.ts): kod MH2026HS przypisany do e-maila użytkownika;
 *   potem „Skopiuj”, a po skopiowaniu „Przejdź do aplikacji” (strona Tokeny AI w audycie).
 * - AuditAdBar (dashboard): otwiera popup sam, chyba że zamknięto go w ostatnich 30 dniach (localStorage).
 * - AuditAdSidebarCard: mała karta na dole rozwiniętego menu (bez opisu), klik otwiera popup.
 * Kreacja losowana raz na sesję (sessionStorage).
 */
type AuditAdId = 'ai' | 'google';

/** Reklama dotyczy widoczności w AI: tylko kreacja „ai” (kreacja „google” zostaje w słownikach). */
const AD_IDS: AuditAdId[] = ['ai'];
const PHOTOS: Record<AuditAdId, string> = { ai: aiPhoto, google: positionsPhoto };
const AUDIT_URL = 'https://audit.hotelspot.pl';
/** Strona Tokeny AI w audycie (routes.ts audytu: tokens), gdzie realizuje się kod. */
const AUDIT_TOKENS_PATH: Record<string, string> = { pl: '/konto/tokeny', en: '/en/account/tokens', de: '/de/konto/tokens' };
const FUNCTIONS_URL = (import.meta.env.VITE_URL || 'https://europe-west1-hotelspot-dev.cloudfunctions.net').replace(/\/+$/, '');
const CREATIVE_KEY = 'hs_audit_ad_creative';
const HIDE_DAYS = 30;
/** Karta w sidebarze zamknięta krzyżykiem: nie pokazujemy jej więcej (w tej przeglądarce). */
const SIDEBAR_HIDDEN_KEY = 'hs_audit_ad_sidebar_hidden';

/** „gwiazdka” promocyjna: wielokąt z 16 ramionami (SVG), jak w audycie */
const BURST = Array.from({ length: 32 }, (_, i) => {
  const r = i % 2 ? 41 : 50;
  const a = (Math.PI * i) / 16;
  return `${(50 + r * Math.sin(a)).toFixed(1)},${(50 - r * Math.cos(a)).toFixed(1)}`;
}).join(' ');

const ss = {
  get: (k: string): string | null => {
    try { return sessionStorage.getItem(k); } catch { return null; }
  },
  set: (k: string, v: string): void => {
    try { sessionStorage.setItem(k, v); } catch { /* prywatne okno */ }
  },
};

const ls = {
  get: (k: string): string | null => {
    try { return localStorage.getItem(k); } catch { return null; }
  },
  set: (k: string, v: string): void => {
    try { localStorage.setItem(k, v); } catch { /* prywatne okno */ }
  },
};

const isHiddenNow = (): boolean => {
  const until = Number(ls.get(AUDIT_AD_HIDDEN_UNTIL_KEY));
  return Number.isFinite(until) && until > Date.now();
};

const pickCreative = (): AuditAdId => {
  const saved = ss.get(CREATIVE_KEY) as AuditAdId | null;
  if (saved && AD_IDS.includes(saved)) return saved;
  const id = AD_IDS[Math.floor(Math.random() * AD_IDS.length)] ?? 'ai';
  ss.set(CREATIVE_KEY, id);
  return id;
};

/** Wspólna treść kreacji (popup i karta w sidebarze): te same klasy hsad-*, różni je skala w stylach. */
const renderCreative = (id: AuditAdId, t: (key: string) => string, withSub = true, overlay: ReactNode = null): ReactNode => (
  <>
    <span className="hsad-stripes" aria-hidden="true" />
    <span className="hsad-logo">
      <b>hotelspot</b>
      <small>{t('auditAd.logoTag')}</small>
    </span>
    <span className="hsad-copy">
      <span className="hsad-head">{t(`auditAd.${id}.head`)}</span>
      {withSub && <span className="hsad-sub">{t(`auditAd.${id}.sub`)}</span>}
    </span>
    <span className="hsad-photo">
      <img src={PHOTOS[id]} alt="" loading="lazy" />
      <i aria-hidden="true" />
      <span className="hsad-burst" aria-hidden="true">
        <svg viewBox="0 0 100 100"><polygon points={BURST} /></svg>
        <span>{t(`auditAd.${id}.badge`)}</span>
      </span>
      {overlay}
    </span>
  </>
);

/** Stan popupu wspólny dla dashboardu i sidebara (host modalu siedzi w Sidebarze). */
const useAuditAdStore = create<{ open: boolean; show:() => void; hide: () => void }>((set) => ({
  open: false,
  show: () => set({ open: true }),
  hide: () => set({ open: false }),
}));

type ClaimState =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'error' }
  | { kind: 'code'; code: string; copied: boolean };

/** v3-auditAccount panelGift: kod specjalny dla e-maila zalogowanego użytkownika (jeden na adres). */
const claimPanelGift = async (hotelName: string, hotelId: string | null): Promise<string> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('unauthorized');
  const res = await fetch(`${FUNCTIONS_URL}/v3-auditAccount`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action: 'panelGift', hotelName, hotelId }),
  });
  const json = await res.json().catch(() => null);
  const code = json?.data?.code ?? json?.code;
  if (!res.ok || typeof code !== 'string') throw new Error(json?.error?.code || 'server');
  return code;
};

/** Interaktywny popup reklamy: kreacja, „Odbierz kod”, „Skopiuj”, „Przejdź do aplikacji”. */
export const AuditAdModalHost: FC = () => {
  const { t, i18n } = useTranslation();
  const { open, hide } = useAuditAdStore();
  const [id] = useState<AuditAdId>(pickCreative);
  const [claim, setClaim] = useState<ClaimState>({ kind: 'idle' });

  const { organizationId } = useOrganizationState();
  const { organizations, currentUser } = useUserState();
  const { data: userOrganizations } = useGetOrganizationsByIds(organizations);
  const hotelName = (userOrganizations?.find((org: any) => org.key === organizationId)?.label as string | undefined) || '';

  const lang = (i18n.language || 'pl').slice(0, 2);
  const tokensUrl = `${AUDIT_URL}${AUDIT_TOKENS_PATH[lang] ?? AUDIT_TOKENS_PATH.pl}`;

  const close = () => {
    ls.set(AUDIT_AD_HIDDEN_UNTIL_KEY, String(Date.now() + HIDE_DAYS * 24 * 60 * 60 * 1000));
    hide();
  };

  const handleClaim = async () => {
    setClaim({ kind: 'busy' });
    try {
      const code = await claimPanelGift(hotelName, organizationId);
      setClaim({ kind: 'code', code, copied: false });
    } catch {
      setClaim({ kind: 'error' });
    }
  };

  const handleCopy = () => {
    if (claim.kind !== 'code') return;
    const done = () => setClaim({ ...claim, copied: true });
    try {
      navigator.clipboard?.writeText(claim.code).then(done).catch(done);
    } catch {
      done();
    }
  };

  const renderClaim = () => {
    if (claim.kind === 'code') {
      return (
        <>
          <span className="hsad-code-label">{t('auditAd.codeLabel')}</span>
          <div className="hsad-code-box">
            <span className="hsad-code-value">{claim.code}</span>
            <button type="button" className="hsad-btn-secondary" onClick={handleCopy}>
              {claim.copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
              {claim.copied ? t('auditAd.codeCopied') : t('auditAd.codeCopy')}
            </button>
          </div>
          <span className="hsad-code-hint">
            {t('auditAd.codeHint', { email: currentUser?.email || '' })}
          </span>
          {claim.copied && (
            <a className="hsad-btn-primary" href={tokensUrl} target="_blank" rel="noopener noreferrer">
              {t('auditAd.goToApp')}
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          )}
        </>
      );
    }
    return (
      <>
        <button
          type="button"
          className="hsad-btn-primary"
          onClick={handleClaim}
          disabled={claim.kind === 'busy'}
        >
          {claim.kind === 'busy' && <LoaderCircle size={16} className="animate-spin" aria-hidden="true" />}
          {claim.kind === 'busy' ? t('auditAd.claiming') : t('auditAd.claim')}
        </button>
        {claim.kind === 'error' && <span className="hsad-code-error" role="alert">{t('auditAd.claimError')}</span>}
      </>
    );
  };

  return (
    <Modal
      isOpen={open}
      onClose={close}
      placement="center"
      hideCloseButton
      classNames={{
        base: 'w-[360px] max-w-[calc(100vw-32px)] rounded-none bg-white shadow-2xl m-0',
        wrapper: 'z-[60]',
        backdrop: 'z-[59]',
      }}
    >
      <ModalContent>
        <AdRoot aria-label={t('auditAd.label')}>
          <div className="hsad-top">
            <span className="hsad-label">{t('auditAd.label')}</span>
            <button
              type="button"
              className="hsad-x"
              onClick={close}
              aria-label={t('auditAd.close')}
              title={t('auditAd.close')}
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
          {/* Odbiór kodu na samej kreacji (w miejscu CTA na zdjęciu), nie pod nią. */}
          <div className={`hsad-box hsad-box-static hsad-theme-${id}`}>
            {renderCreative(id, t, true, <span className={`hsad-claim${claim.kind === 'code' ? ' is-code' : ''}`}>{renderClaim()}</span>)}
          </div>
        </AdRoot>
      </ModalContent>
    </Modal>
  );
};

/** Dashboard: otwiera popup przy wejściu, chyba że zamknięto go w ostatnich 30 dniach. */
export const AuditAdBar: FC = () => {
  const show = useAuditAdStore((s) => s.show);
  useEffect(() => {
    if (!isHiddenNow()) show();
  }, [show]);
  return null;
};

/** Mała karta na dole rozwiniętego sidebara (bez opisu): klik otwiera interaktywny popup. */
export const AuditAdSidebarCard: FC = () => {
  const { t } = useTranslation();
  const [id] = useState<AuditAdId>(pickCreative);
  const show = useAuditAdStore((s) => s.show);
  const [hidden, setHidden] = useState(() => ls.get(SIDEBAR_HIDDEN_KEY) === '1');

  if (hidden) return null;

  return (
    <AdCardRoot aria-label={t('auditAd.label')}>
      <div className="hsad-top">
        <span className="hsad-label">{t('auditAd.label')}</span>
        <button
          type="button"
          className="hsad-x"
          onClick={() => { ls.set(SIDEBAR_HIDDEN_KEY, '1'); setHidden(true); }}
          aria-label={t('auditAd.close')}
          title={t('auditAd.close')}
        >
          <X size={12} aria-hidden="true" />
        </button>
      </div>
      <button type="button" className={`hsad-box hsad-theme-${id}`} onClick={show}>
        {renderCreative(id, t, false)}
        <span className="hsad-mini-burst" aria-hidden="true">
          <svg viewBox="0 0 100 100"><polygon points={BURST} /></svg>
          <span>+1000</span>
        </span>
      </button>
    </AdCardRoot>
  );
};
